import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import { useMediaQuery, useTheme, Typography, Link } from '@mui/material';
import ReactTooltip from 'react-tooltip';

import { imgLinks, useReady } from '../../shared';
import useConsole from '../../hooks/useConsole';

import {
  StyledAwarePartnerHeader,
  StyledAwareAboutInfoContainer,
  StyledOnlineVetCoverImage,
  StyledContentTextHeader,
  StyledVoucherContainer,
  StyledVoucherCopyContainer,
  StyledIconButton,
  StyledContainer,
  StyledBackButton,
  StyledPaper,
  StyledItemContainer,
  StyledNumberImg,
  StyledArrowImg,
  StyledItemsContainer,
  StyledSectionTwoContainer,
} from './AwarePartner.styled';

const renderLink = (chunk, href) => (
  <Link variant="p" href={href} target="_blank">
    {chunk}
  </Link>
);
const AwarePartner = () => {
  const ready = useReady();
  const awarePartnerHomeLink =
    'https://www.aware.app/de/shop/1c0a5c29-7805-40ff-9fdf-053b5e1a85ea/LTH';
  const awarePartnerVoucherLink = 'https://www.aware.app/de/gesundheitspakete';
  const textRef = useRef(null);
  const buttonRef = useRef(null);
  const { consoleError } = useConsole();
  const intl = useIntl();

  const theme = useTheme();

  const isSmScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isMdScreen = useMediaQuery(theme.breakpoints.between('tablet', 'desktop'));
  const isLgScreen = useMediaQuery(theme.breakpoints.between('desktop', 'largeDesktop'));
  const isXlScreen = useMediaQuery(theme.breakpoints.up('largeDesktop'));

  const getAwareHeaderImg = () => {
    switch (true) {
      case isXlScreen:
        return imgLinks['aware-header-xl'];
      case isLgScreen:
        return imgLinks['aware-header-lg'];
      case isMdScreen:
        return imgLinks['aware-header-md'];
      case isSmScreen:
        return imgLinks['aware-header-sm'];
      default:
        return imgLinks['aware-header-lg'];
    }
  };

  const handleCopy = () => {
    if (!window?.navigator?.clipboard) return;

    const textToCopy = textRef.current?.innerText || '';
    if (!textToCopy) return;
    window.navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        ReactTooltip.show(buttonRef.current);
        setTimeout(() => {
          ReactTooltip.hide(buttonRef.current);
        }, 1000);
      })
      .catch((err) => consoleError('Copy failed', err));
  };
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCopy();
    }
  };
  return (
    <CSSTransition in={ready} timeout={600} classNames="slow-fade" unmountOnExit>
      <>
        <StyledAwarePartnerHeader>
          <StyledBackButton
            to="/health-partner-info"
            label={<FormattedMessage id="common.button.back" />}
          />

          <StyledOnlineVetCoverImage src={getAwareHeaderImg()} alt="awareHeaderImage" />
        </StyledAwarePartnerHeader>

        <StyledContainer>
          <StyledContentTextHeader>
            <Typography variant="h1">
              {intl.formatMessage(
                { id: 'vitolo.partner.page.aware.title1' },
                {
                  percentage: (
                    <Typography variant="h1" color="lavenderIndigo">
                      <FormattedMessage id="vitolo.partner.page.aware.title2" />
                    </Typography>
                  ),
                }
              )}
            </Typography>
          </StyledContentTextHeader>

          <StyledAwareAboutInfoContainer>
            <Typography variant="h2">
              <FormattedMessage id="vitolo.partner.page.aware.about.title" />
            </Typography>
            <Typography variant="p">
              <FormattedMessage
                id="vitolo.partner.page.aware.about.description"
                values={{
                  a: (chunk) => renderLink(chunk, awarePartnerHomeLink),
                }}
              />
            </Typography>
          </StyledAwareAboutInfoContainer>

          <StyledAwareAboutInfoContainer>
            <Typography variant="h2">
              <FormattedMessage id="vitolo.partner.page.aware.benefits.title" />
            </Typography>
            <Typography variant="p">
              {intl.formatMessage(
                { id: 'vitolo.partner.page.aware.benefits.description' },
                {
                  longtermText: (
                    <Typography variant="h3" component="span">
                      <FormattedMessage id="vitolo.partner.page.aware.benefits.description.longterm" />
                    </Typography>
                  ),
                }
              )}
            </Typography>
          </StyledAwareAboutInfoContainer>
          <StyledItemsContainer>
            <Typography variant="h2">
              <FormattedMessage id="vitolo.partner.page.sunday.voucher.placeholder" />
            </Typography>
            <StyledPaper>
              <StyledItemContainer>
                <StyledNumberImg src={imgLinks['icon.aware.one']} alt="item-one" />
                <Typography variant="h3">
                  <FormattedMessage id="vitolo.partner.page.aware.section1.title" />
                </Typography>
              </StyledItemContainer>
              <Typography variant="p">
                <FormattedMessage
                  id="vitolo.partner.page.aware.section1.description"
                  values={{
                    a: (chunk) => renderLink(chunk, awarePartnerVoucherLink),
                  }}
                />
              </Typography>
              <StyledVoucherContainer>
                <Typography variant="p">
                  <FormattedMessage id="vitolo.partner.page.aware.voucher.label" />
                </Typography>
                <StyledVoucherCopyContainer role="button" tabIndex={0} onKeyDown={handleKeyDown}>
                  <Typography variant="h3" ref={textRef}>
                    <FormattedMessage id="vitolo.partner.page.aware.voucher" />
                  </Typography>
                  <ReactTooltip
                    id="copyTooltip"
                    place="top"
                    effect="solid"
                    className="u3-referral-tooltip"
                  >
                    <Typography variant="p">
                      <FormattedMessage id="vitolo.partner.page.link.copied" />
                    </Typography>
                  </ReactTooltip>
                  <StyledIconButton
                    icon="icon.aware.copy"
                    data-tip
                    data-for="copyTooltip"
                    data-event="none"
                    ref={buttonRef}
                    onClick={handleCopy}
                  />
                </StyledVoucherCopyContainer>
              </StyledVoucherContainer>
            </StyledPaper>
            <StyledArrowImg src={imgLinks['icon.aware.right.arrow']} alt="arrow-left" />

            <StyledPaper>
              <StyledItemContainer>
                <StyledNumberImg src={imgLinks['icon.aware.two']} alt="item-two" />
                <Typography variant="h3">
                  <FormattedMessage id="vitolo.partner.page.aware.section2.title" />
                </Typography>
              </StyledItemContainer>
              <StyledSectionTwoContainer>
                <Typography variant="p">
                  <FormattedMessage id="vitolo.partner.page.aware.section2.description" />
                </Typography>
                <img src={imgLinks['icon.aware.arrow.side']} alt="arrow-side" />
                <img src={imgLinks['icon.aware.shopping.cart']} alt="arrow-side" />
              </StyledSectionTwoContainer>
            </StyledPaper>
            <StyledArrowImg src={imgLinks['icon.aware.left.arrow']} alt="arrow-left" />

            <StyledPaper>
              <StyledItemContainer>
                <StyledNumberImg src={imgLinks['icon.aware.three']} alt="item-three" />
                <Typography variant="h3">
                  <FormattedMessage id="vitolo.partner.page.aware.section3.title" />
                </Typography>
              </StyledItemContainer>
              <Typography variant="p">
                <FormattedMessage id="vitolo.partner.page.aware.section3.description" />
              </Typography>
            </StyledPaper>
            <StyledArrowImg src={imgLinks['icon.aware.right.arrow']} alt="arrow-left" />

            <StyledPaper>
              <StyledItemContainer>
                <StyledNumberImg src={imgLinks['icon.aware.four']} alt="item-four" />
                <Typography variant="h3">
                  <FormattedMessage id="vitolo.partner.page.aware.section4.title" />
                </Typography>
              </StyledItemContainer>
              <Typography variant="p">
                <FormattedMessage id="vitolo.partner.page.aware.section4.description" />
              </Typography>
            </StyledPaper>
            <StyledArrowImg src={imgLinks['icon.aware.left.arrow']} alt="arrow-left" />
            <StyledPaper>
              <StyledItemContainer>
                <StyledNumberImg src={imgLinks['icon.aware.five']} alt="item-five" />
                <Typography variant="h3">
                  <FormattedMessage id="vitolo.partner.page.aware.section5.title" />
                </Typography>
              </StyledItemContainer>
              <Typography variant="p">
                <FormattedMessage id="vitolo.partner.page.aware.section5.description" />
              </Typography>
            </StyledPaper>
            <StyledArrowImg src={imgLinks['icon.aware.right.arrow']} alt="arrow-left" />

            <StyledPaper>
              <StyledItemContainer>
                <StyledNumberImg src={imgLinks['icon.aware.six']} alt="item-six" />
                <Typography variant="h3">
                  <FormattedMessage id="vitolo.partner.page.aware.section6.title" />
                </Typography>
              </StyledItemContainer>
              <Typography variant="p">
                <FormattedMessage
                  id="vitolo.partner.page.aware.section6.description"
                  values={{
                    a: (chunk) => renderLink(chunk, '/user-claims-info'),
                  }}
                />
              </Typography>
            </StyledPaper>
            <Typography variant="legalTexts">
              <FormattedMessage id="vitolo.partner.page.aware.last.description" />
            </Typography>
          </StyledItemsContainer>
        </StyledContainer>
      </>
    </CSSTransition>
  );
};

export default AwarePartner;
